import { useState, useEffect, useRef, useContext, useMemo } from "react";

//Components
import { FormControl, Checkbox, FormControlLabel } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import SingleDropDown from "./SingleDropDown";
import PhoneInput from "./PhoneInput";

//Contexts
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { MutationContext } from "../Contexts/MutationContext";

//Styling
import { useStyles } from "../Styles/styles";
import deleteButton from "../Assets/deleteButton.png";

//Utils
import { UserContext } from "../Contexts/UserContext";
import AddressWithAlertAndHmi from "./AddressWithAlertAndHmi";
import PersonHistoryAlert from "./PersonHistoryAlert";
import { shouldBeLocked } from "../utils/Roles";

import moment from "moment";

const Person = ({
  pis,
  incidentInvolvements,
  numOfPersons,
  removePerson,
  incidentId,
  incidentUuid,
  incidentIndex,
  pisIndex,
  handleIncidentOpening,
  handleTicketOpening,
  handlePersonOpening,
  disabled = undefined,
  userRoles,
  setPausePolling,
  setPopper,
  aliasView = false,
  showAliases = false,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const { person } = pis;
  const {
    UPDATE_PIS,
    UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
    UPDATE_PERSON_IN_PIS,
    ADD_OR_UPDATE_PIS,
  } = INCIDENT_ACTIONS;
  const { incidentDispatch, incidents } = useContext(IncidentsContext);
  const {
    updatePisIncidentInvolvement,
    updatePersonOrganization,
    updatePersonDob,
    updatePersonHomeAddress,
    throttledAddAddressAlertMutation,
    removeAddressAlert,
    throttledAddPersonAlert,
    removePersonAlert,
    updatePersonPhone,
  } = useContext(MutationContext);
  const { user, tokenDisabled } = useContext(UserContext);

  const classes = useStyles();
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const incident =
    incidents?.find(incident => incident?.id?.toString() === incidentId) ||
    null;
  const address = {
    id:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.id
        : "",
    city:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.city
        : "",
    lat:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.lat
        : "",
    lng:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.lng
        : "",
    addressName:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.name
        : "",
    state:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.state
        : "",
    streetName:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.streetName
        : "",
    streetNumber:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.streetNumber
        : "",
    unit:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.unit
        : "",
    zip:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person.personalAttribute.homeAddress.zip
        : "",
    validAddressAlerts:
      pis?.person && pis?.person.personalAttribute?.homeAddress
        ? pis?.person &&
          pis?.person.personalAttribute?.homeAddress?.validAddressAlerts
        : [],
  };
  const [showNameModal, setShowNameModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleDateChange = (value, name, date) => {
    let fieldToUpdate = stateToMutation[name];
    let serverTimeFormat = "";
    if (!date) {
      incidentDispatch({
        type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
        payload: {
          incidentId: incidentId,
          pisId: pis?.id,
          pisUuid: pis?.uuid,
          personId: pis?.person?.id,
          personUuid: pis?.person?.uuid,
          property: name,
          value: serverTimeFormat,
        },
      });
      updatePersonDob({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incidentId ? incidentId : null,
            personId: pis?.person?.id ? pis?.person?.id : pis?.person?.uuid,
            [fieldToUpdate]: serverTimeFormat,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    } else if (date.isValid()) {
      serverTimeFormat = date.utc().format("YYYY-MM-DD HH:mm:ss UTC");
      incidentDispatch({
        type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
        payload: {
          incidentId: incidentId,
          pisId: pis?.id,
          pisUuid: pis?.uuid,
          personId: pis?.person?.id,
          personUuid: pis?.person?.uuid,
          property: name,
          value: serverTimeFormat,
        },
      });
      updatePersonDob({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incidentId ? incidentId : null,
            personId: pis?.person?.id ? pis?.person?.id : pis?.person?.uuid,
            [fieldToUpdate]: serverTimeFormat,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    }
  };

  useEffect(() => {
    if (inputLabel.current && inputLabel.current.offsetWidth) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  const stateToMutation = {
    incidentInvolvement: "incidentInvolvementId",
    organization: "organization",
    dob: "dob",
  };

  const handleChange = (event) => {
    let name = event.target.name;
    let value = event.target.value;
    let fieldToUpdate = stateToMutation[name];

    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated: ${name}`
      );
      return null;
    }
    switch (name) {
      case "incidentInvolvement":
        incidentDispatch({
          type: UPDATE_PIS,
          payload: {
            incidentId: incidentId,
            pisId: pis?.id,
            pisUuid: pis?.uuid,
            property: name,
            value: value,
          },
        });
        updatePisIncidentInvolvement({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : incidentUuid,
              personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
              personIncidentSpecificId: pis?.id ? pis.id : pis?.uuid,
              [fieldToUpdate]: value.id,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
        return null;

      default:
        alert(`Name not found in handleChange function in Person.js: ${name}`);
        return null;
    }
  };

  const handleCheckbox = e => {
    let bool = e.target.checked;
    let name = e.target.value;
    let fieldToUpdate = stateToMutation[name];

    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated: ${name}`
      );
      return null;
    }
    switch (name) {
      case "organization":
        incidentDispatch({
          type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
          payload: {
            incidentId: incidentId,
            pisId: pis?.id,
            pisUuid: pis?.uuid,
            personId: pis?.person?.id,
            personUuid: pis?.person?.uuid,
            property: name,
            value: bool,
          },
        });
        updatePersonOrganization({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : null,
              personId: pis?.person?.id ? pis?.person?.id : pis?.person?.uuid,
              [fieldToUpdate]: bool,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
        return null;

      default:
        alert(
          `Name not found in handleCheckbox function in Person.js: ${name}`
        );
        return null;
    }
  };

  const updateAddress = address => {
    const input = {
      personId: person.id,
      homeAddressId: address?.id ? address.id : "",
      originatorId: user?.id,
      incidentId: incidentId ? incidentId : null,
    };
    incidentDispatch({
      type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      payload: {
        incidentId: incidentId,
        pisId: pis?.id,
        pisUuid: pis?.uuid,
        personId: pis?.person?.id,
        personUuid: pis?.person?.uuid,
        property: "homeAddress",
        value: address,
      },
    });
    updatePersonHomeAddress({
      variables: { input },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const handleAddAddressAlert = alert => {
    throttledAddAddressAlertMutation({
      variables: {
        input: {
          addressId: pis?.person.personalAttribute?.homeAddress?.id,
          officerId: user?.id,
          originatorId: user?.id,
          addressAlertId: alert.id,
          alertTypeId: alert?.alertType?.id,
          notes: alert?.notes,
          expireAt: alert?.expireAt,
          incidentId: incidentId ? incidentId : null,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts = [
      ...(pis?.person.personalAttribute?.homeAddress?.validAddressAlerts || []),
    ];
    localAlerts.push(alert);
    let homeAddressCopy = JSON.parse(
      JSON.stringify(pis?.person.personalAttribute?.homeAddress)
    );
    homeAddressCopy.validAddressAlerts = localAlerts;
    incidentDispatch({
      type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      payload: {
        personId: pis?.person?.id,
        personUuid: pis?.person?.uuid,
        property: "homeAddress",
        value: homeAddressCopy,
      },
    });
  };

  const handleRemoveAddressAlert = alertId => {
    removeAddressAlert({
      variables: { input: { addressAlertId: alertId, originatorId: user?.id } },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts =
      pis?.person.personalAttribute?.homeAddress?.validAddressAlerts?.filter(
        vaa => vaa.id.toString() !== alertId.toString()
      ) || [];
    let homeAddressCopy = JSON.parse(
      JSON.stringify(pis?.person.personalAttribute?.homeAddress)
    );
    homeAddressCopy.validAddressAlerts = localAlerts;

    incidentDispatch({
      type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      payload: {
        personId: pis?.person?.id,
        personUuid: pis?.person?.uuid,
        property: "homeAddress",
        value: homeAddressCopy,
      },
    });
  };

  const handleAddPersonAlert = alert => {
    throttledAddPersonAlert({
      variables: {
        input: {
          personId: pis?.person?.id,
          officerId: user?.id,
          originatorId: user?.id,
          personAlertId: alert.id,
          alertTypeId: alert?.alertType?.id,
          notes: alert?.notes,
          expireAt: alert?.expireAt,
          incidentId: incidentId ? incidentId : null,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts = [...(pis?.person?.validPersonAlerts || [])];
    localAlerts.push(alert);
    incidentDispatch({
      type: UPDATE_PERSON_IN_PIS,
      payload: {
        incidentId: incidentId ? incidentId : null,
        incidentUuid: incidentUuid ? incidentUuid : null,
        pisId: pis?.id,
        pisUuid: pis?.uuid,
        property: "validPersonAlerts",
        value: localAlerts,
      },
    });
  };

  const handleAddPersonMatchToIncidentState = (
    incidentId,
    personIncidentSpecific
  ) => {
    incidentDispatch({
      type: ADD_OR_UPDATE_PIS,
      payload: {
        incidentId: incidentId,
        pis: personIncidentSpecific,
      },
    });
  };

  const handleRemovePersonAlert = alertId => {
    removePersonAlert({
      variables: { input: { personAlertId: alertId, originatorId: user?.id } },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts = pis?.person?.validPersonAlerts?.filter(
      vpa => vpa.id.toString() !== alertId.toString()
    ) || [];
    incidentDispatch({
      type: UPDATE_PERSON_IN_PIS,
      payload: {
        incidentId: incidentId ? incidentId : null,
        incidentUuid: incidentUuid ? incidentUuid : null,
        pisId: pis?.id,
        pisUuid: pis?.uuid,
        property: "validPersonAlerts",
        value: localAlerts,
      },
    });
  };

  const sendPhoneUpdate = (phoneNumber, propertyName) => {
    incidentDispatch({
      type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      payload: {
        incidentId: incidentId,
        pisId: pis?.id,
        pisUuid: pis?.uuid,
        personId: pis?.person?.id,
        personUuid: pis?.person?.uuid,
        property: propertyName,
        value: phoneNumber,
      },
    });
    updatePersonPhone({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId ? incidentId : null,
          personId: pis?.person?.id,
          [propertyName]: phoneNumber,
        },
      },
    });
  };

  return (
    <div
      data-cy={"person-div-wrapper-" + pisIndex}
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "flex-start",
        width: "100%",
        border: "1px solid grey",
        borderRadius: "5px",
        marginBottom: "4vh",
      }}
      autoComplete="off"
    >
      <SingleDropDown
        handleChange={e => handleChange(e, true)}
        stateName={"incidentInvolvement"}
        value={pis?.incidentInvolvement || null}
        options={incidentInvolvements}
        title={"Person\xa0Type"}
        classStyle={classes.textfieldMarginAndFlexEnd}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <FormControl
        data-cy={"business-checkbox-FormControl-" + pisIndex}
        variant="outlined"
        className={classes.formControl}
      >
        <FormControlLabel
          style={{ margin: "auto" }}
          control={
            <Checkbox
              inputProps={{ "data-cy": "business-checkbox-" + pisIndex }}
              checked={person?.personalAttribute?.organization || false}
              onChange={handleCheckbox}
              value="organization"
              color="primary"
              disabled={memoizedLocked}
            />
          }
          label="Bus."
        />
      </FormControl>

      <PersonHistoryAlert
        setPopper={setPopper}
        pis={pis}
        business={person?.personalAttribute?.organization}
        showNameModal={showNameModal}
        setShowNameModal={setShowNameModal}
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        incidentId={incidentId}
        incidentIndex={incidentIndex}
        pisId={pis?.id}
        pisUuid={pis?.uuid}
        pisIndex={pisIndex}
        person={pis?.person}
        addAlert={handleAddPersonAlert}
        removeAlert={handleRemovePersonAlert}
        handleIncidentOpening={handleIncidentOpening}
        handleTicketOpening={handleTicketOpening}
        handlePersonOpening={handlePersonOpening}
        handleAddPersonMatchToIncidentState={
          handleAddPersonMatchToIncidentState
        }
        showHistory={!aliasView}
        showAlert={false || !aliasView}
        showAliases={showAliases}
        userRoles={userRoles}
        user={user}
        setPausePolling={setPausePolling}
        inIncident={!!incident}
        incidentOriId={incident?.ori?.id}
        tokenDisabled={tokenDisabled}
        {...props}
      />

      <KeyboardDatePicker
        data-cy={"person-dob-picker-" + pis?.person?.id}
        inputProps={{ "data-cy": "person-dob-picker-input-" + pis?.person?.id }}
        InputLabelProps={{ shrink: true }}
        clearable
        label="DOB"
        value={
          pis?.person?.personalAttribute?.dob
            ? moment
                .utc(
                  pis?.person?.personalAttribute?.dob,
                  "YYYY-MM-DD HH:mm:ss UTC"
                )
                .local()
            : null
        }
        onChange={(date, value) => handleDateChange(value, "dob", date)}
        format="MM/DD/YYYY"
        style={{
          width: "150px",
          marginRight: "1vw",
          visibility: person?.personalAttribute?.organization
            ? "hidden"
            : "visible",
        }}
        className={classes.smallMatchMargin}
        disabled={memoizedLocked}
      />
      <PhoneInput
        setPhoneNumber={sendPhoneUpdate}
        phoneString={pis?.person?.personalAttribute?.phone || ""}
        pisId={pis?.id ? pis.id : pis?.uuid}
        personId={pis?.person?.id ? pis?.person?.id : pis?.person?.uuid}
        modalClass={classes.modal}
        inputClass={classes.modalInput}
        paperClass={classes.paper}
        title={"Phone"}
        propertyName={"phone"}
        textFieldClass={classes.smallMatchMargin}
        style={{ marginRight: "1vw" }}
        userRoles={userRoles}
        {...props}
      />

      {!aliasView && (
        <>
          <AddressWithAlertAndHmi
            address={address}
            showHmi={true}
            showAlert={false}
            addressInputLabel={"Address"}
            setAddress={updateAddress}
            validAlertName={"validAddressAlerts"}
            alertType={"address"}
            deleteAlert={handleRemoveAddressAlert}
            addAlert={handleAddAddressAlert}
            userRoles={userRoles}
            onFocus={() => setPausePolling(true)}
            onBlur={() => setPausePolling(false)}
            {...props}
          />
          <FormControl
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
            className={classes.formControl}
          >
            {/* {numOfPersons > 1 ? <Fab data-cy={"delete-person-button-" + pis?.person?.id} className={classes.fabButton} onClick={() => removePerson(pis.id)}><DeleteForeverIcon /></Fab> : null} */}
            {/* <Fab data-cy={"delete-person-button-" + pis?.person?.id} className={classes.fabButton} onClick={() => removePerson(pis.id, numOfPersons)}><DeleteForeverIcon /></Fab> */}
            <button
              type="button"
              onClick={!memoizedLocked ? () => removePerson(pis) : null}
              disabled={memoizedLocked}
              style={{ background: "none", border: 0, padding: 0 }}
            >
              <img
                data-cy={"delete-person-button-" + pis?.person?.id}
                src={deleteButton}
                className={classes.pngButton}
                alt="Delete Person"
              />
            </button>
            {/* {pisIndex === numOfPersons - 1 ? <Fab data-cy={"add-person-button-" + pis?.person?.id} className={classes.fabButton} onClick={handleAddPISToIncident}><AddCircleOutlineIcon /></Fab> : null} */}
          </FormControl>
        </>
      )}
    </div>
  );
};

export default Person;
